import { IMPORT_TYPES } from './massImport.constants';

/**
 * Helper function to get field key from row (checks title first, then id)
 * @param {Object} row - Row data
 * @param {Object} field - Field object with title and id
 * @returns {string|null} - Field key or null
 */
const getFieldKey = (row, field) => {
  if (row[field.title] !== undefined) return field.title;
  if (row[field.id] !== undefined) return field.id;
  return null;
};

/** Get project from row's main project/Project column using projectMap, or null. */
const getProjectFromMainFields = (row, context) => {
  return context.projectMap?.[row.name.trim()] ?? null;
};

/** Get first project found in custom field sections (Projects dropdowns), or null. */
const getProjectFromCustomFieldSections = (row, customFieldSections, context) => {
  if (!customFieldSections?.length) return null;
  for (const section of customFieldSections) {
    for (const field of section.fields || []) {
      if (field.type !== 'dropdown' || field.configProps?.dataType !== 'Projects') continue;
      const fieldKey = getFieldKey(row, field);
      if (!fieldKey || row[fieldKey] == null) continue;
      const project = context.projectMap?.[String(row[fieldKey]).trim()];
      if (project) return project;
    }
  }
  return null;
};

/**
 * Helper function to find project from row data (checks both main fields and custom fields)
 * @param {Object} row - Row data from spreadsheet
 * @param {Array} customFieldSections - Custom field sections (optional)
 * @param {Object} context - Context with projectMap
 * @returns {Object|null} - Project object or null
 */
const getProjectFromRow = (row, customFieldSections, context) => {
  if (!row || !context.projectMap) return null;

  return getProjectFromMainFields(row, context)
    ?? getProjectFromCustomFieldSections(row, customFieldSections, context);
};

/**
 * Verifies that a value is a valid option for a single Custom dropdown field (no section scan).
 * @param {Object} field - The dropdown field config (with configProps.customOptions)
 * @param {string} trimmedValue - Trimmed value to match (by option.id)
 * @returns {Object|null} - Custom option or null
 */
const verifyCustomOption = (field, trimmedValue) => {
  return field?.configProps?.customOptions?.find((opt) => opt.id === trimmedValue) ?? null;
};

const getProjectForCostcodeImport = (row, customFieldSections, context) => {
  const fromCustomField = getProjectFromCustomFieldSections(row, customFieldSections, context);
  if (fromCustomField) return fromCustomField;
  if (context.importType === IMPORT_TYPES.PROJECTS) {
    return getProjectFromMainFields(row, context);
  }
  return null;
};

const resolveImportedCostcodePhaseId = (matched, phases, project) => {
  const projectId = matched.projectId || project?.id;
  if (!matched.id || !projectId) return { phaseId: 'unphased' };

  const phaseIds = new Set();
  (phases || []).forEach((phase) => {
    if (phase.costcodeId === matched.id && phase.projectId === projectId && phase.id) {
      phaseIds.add(phase.id);
    }
  });
  const uniquePhaseIds = Array.from(phaseIds);
  if (uniquePhaseIds.length > 1) {
    const label = matched.code || matched.name;
    return { error: `Cost code "${label}" is assigned to multiple phases` };
  }
  return { phaseId: uniquePhaseIds[0] || 'unphased' };
};

const findImportedCostcode = (trimmedValue, context, row, customFieldSections) => {
  const options = context.costcodeOptions || [];
  const matches = options.filter((option) => (
    option.name === trimmedValue || option.code === trimmedValue
  )).map((option) => ({
    id: option.id || option.value,
    name: option.name,
    code: option.code,
    projectId: option.projectId,
  }));
  const project = getProjectForCostcodeImport(row, customFieldSections, context);

  let matched = null;
  if (!matches.length) {
    matched = context.costcodeMap?.[trimmedValue] ?? null;
  } else if (project?.id) {
    matched = matches.find((option) => option.projectId === project.id)
      ?? matches.find((option) => !option.projectId)
      ?? null;
  } else {
    matched = matches.find((option) => !option.projectId) ?? null;
  }
  if (!matched) return null;

  const { phaseId, error } = resolveImportedCostcodePhaseId(matched, context.phases, project);
  if (error) return { error };
  return { ...matched, phaseId };
};

/**
 * Helper function to find dropdown value in appropriate map based on dataType
 * @param {string} importedValue - Value from import
 * @param {string} dataType - Data type (Customers, Projects, etc.)
 * @param {Object} context - Context with lookup maps (and optionally _payableFormId from processCustomFields)
 * @param {Object|null} row - Row data (used for project-specific lookups like SOV Sub-contracts)
 * @param {string|null} subDataType - Sub data type (e.g., board ID for Cards, bucket template ID for Buckets)
 * @param {Array|null} customFieldSections - Custom field sections (used to find project in custom fields)
 * @param {Object|null} currentField - Current dropdown field (for Custom type; avoids scanning all sections)
 * @returns {Object|null} - Matched value object with { id, name } or null
 */
const findDropdownValue = (importedValue, dataType, context, row = null, subDataType = null, customFieldSections = null, currentField = null) => {
  if (!importedValue || !dataType) return null;

  const trimmedValue = String(importedValue).trim();
  if (!trimmedValue) return null;

  const dataTypeMap = {
    Customers: () => context.customerMap?.[trimmedValue],
    Projects: () => context.projectMap?.[trimmedValue],
    Users: () => context.userMap?.[trimmedValue],
    Vendors: () => context.vendorMap?.[trimmedValue],
    Costcodes: () => findImportedCostcode(trimmedValue, context, row, customFieldSections),
    Equipment: () => context.equipmentMap?.[trimmedValue],
    Buckets: () => context.bucketTemplateMap?.[trimmedValue],
    Divisions: () => context.divisionMap?.[trimmedValue],
    Labels: () => context.labelMap?.[trimmedValue],
    Contacts: () => context.contactMap?.[trimmedValue],
    Cards: () => context.cardMap?.[trimmedValue],
    Forms: () => context.formTemplateMap?.[trimmedValue],
    Roles: () => context.roleMap?.[trimmedValue],
    ScheduleCategories: () => context.scheduleCategoryMap?.[trimmedValue],
    'Sub-Contracts': () => {
      const project = getProjectFromRow(row, customFieldSections, context);

      if (!project?.id || !context.scheduleOfValues) return null;

      // Find matching sub-contract in scheduleOfValues
      const matchingEntry = Object.entries(context.scheduleOfValues)
        .map(([rowKey, entries]) => {
          if (!Array.isArray(entries) || entries.length === 0) return null;
          return { rowKey, entry: entries.at(-1)};
        })
        .find((item) => {
          if (!item) return false;
          const { entry } = item;
          return entry?.projectId === project.id
            && entry.isSubContract
            && !entry.isChangeOrder
            && (entry.description || '').trim() === trimmedValue;
        });

      if (!matchingEntry) return null;

      const { entry } = matchingEntry;
      return { id: entry.rowId, name: (entry.description || '').trim() };
    },
    Custom: () => {
      const field = currentField ?? customFieldSections?.flatMap((s) => s.fields || []).find((f) => f?.type === 'dropdown' && f?.configProps?.dataType === 'Custom');
      const customOption = verifyCustomOption(field, trimmedValue);
      if (!customOption) return null;

      return { id: customOption.id, name: customOption.name };
    },
  };

  const lookupFn = dataTypeMap[dataType];
  if (!lookupFn) {
    return null;
  }

  const matched = lookupFn();
  if (!matched) {
    return null;
  }
  if (matched.error) return matched;

  // Costcodes fields persist { id, phaseId, name }. The UI reconstructs
  // `${phaseId}.${id}` on load; globals/unphased use the 'unphased' prefix.
  return {
    id: matched.id,
    name: matched.name || trimmedValue,
    ...(dataType === 'Costcodes' && { phaseId: matched.phaseId || 'unphased' }),
  };
};

/**
 * Helper function to process custom fields for any profile type
 * @param {Object} row - Row data from spreadsheet
 * @param {Array} customFieldSections - Custom field sections from template
 * @param {Object} context - Context with lookup maps
 * @returns {{ customData: Array, invalidCustomFields: Array }} - Processed custom data and any invalid custom field entries
 */
const processCustomFields = (row, customFieldSections, context) => {
  const invalidCustomFields = [];

  if (!customFieldSections || !Array.isArray(customFieldSections)) {
    return { customData: [], invalidCustomFields };
  }

  const customData = customFieldSections
    .map((section) => {
      const processedFields = section.fields
        ?.map((field) => {
          // Only process 'text' and 'dropdown' fields for now
          if (field.type !== 'text' && field.type !== 'dropdown') {
            return null;
          }

          const fieldKey = getFieldKey(row, field);
          const fieldValue = fieldKey ? row[fieldKey] : undefined;

          if (fieldValue === undefined || fieldValue === '') {
            return null;
          }

          let response = { value: '' };

          if (field.type === 'text') {
            response = { value: String(fieldValue) };
          } else if (field.type === 'dropdown') {
            const dataType = field.configProps?.dataType || 'Custom';
            const subDataType = field.configProps?.subDataType;

            // Only support single answer for now
            const importedValue = String(fieldValue).trim();

            const matchedValue = findDropdownValue(importedValue, dataType, context, row, subDataType, customFieldSections, field);

            if (matchedValue?.error) {
              invalidCustomFields.push({
                fieldTitle: field.title,
                sectionName: section.name,
                importedValue,
                dataType,
                reason: matchedValue.error,
              });
              return null;
            }
            if (matchedValue) {
              response = {
                values: [matchedValue],
              };
            } else {
              invalidCustomFields.push({
                fieldTitle: field.title,
                sectionName: section.name,
                importedValue,
                dataType,
                reason: `Value "${importedValue}" not found in ${dataType}`,
              });
              return null;
            }
          }

          return {
            duplicatedParentId: null,
            fieldId: field.id,
            sectionId: field.sectionId || section.id,
            title: field.title,
            type: field.type,
            response,
          };
        })
        .filter((f) => f !== null);

      if (!processedFields || processedFields.length === 0) {
        return null;
      }

      return {
        name: section.name,
        duplicatedParentId: null,
        fields: processedFields,
      };
    })
    .filter((section) => section !== null);

  return { customData, invalidCustomFields };
};

/**
 * Applies custom fields for a payload and throws an error if any are invalid.
 * @param {Object} row - Row data from spreadsheet
 * @param {Array} customFieldSections - Custom field sections from template
 * @param {Object} context - Context with lookup maps
 * @param {Object} payload - Payload to apply custom fields to
 */
export const applyCustomFieldsForPayload = (row, customFieldSections, context, payload) => {
  const { customData, invalidCustomFields } = processCustomFields(row, customFieldSections, context);
  if (invalidCustomFields.length > 0) {
    const errorMessages = invalidCustomFields.map(
      (invalid) => `Custom field "${invalid.fieldTitle}": ${invalid.reason}`,
    );
    throw new Error(errorMessages.join('; '));
  }
  if (customData.length > 0) payload.customData = customData;
};

/**
 * Collects board IDs from custom field sections that have Cards dropdowns (subDataType = board ID).
 * Used to pre-fetch board cards for import so Cards custom fields can resolve values.
 * @param {Array} sections - Custom field sections (e.g. template.sections)
 * @returns {Array<string>} - Array of unique board IDs
 */
export const getBoardIdsFromCustomFieldSections = (sections) => {
  if (!Array.isArray(sections)) return [];
  const boardIds = new Set();
  sections.forEach((section) => {
    section.fields?.forEach((field) => {
      if (
        field.type === 'dropdown'
        && field.configProps?.dataType === 'Cards'
        && field.configProps?.subDataType
      ) {
        boardIds.add(field.configProps.subDataType);
      }
    });
  });
  return Array.from(boardIds);
};

/**
 * Helper function to extract custom field headers from sections
 * @param {Array} sections - Custom field sections
 * @returns {Array} - Array of header objects
 */
export const getCustomFieldHeaders = (sections) => {
  const customFieldHeaders = [];

  sections.forEach((section) => {
    section.fields?.forEach((field) => {
      // Only support 'text' and 'choose one or more' (dropdown) fields
      const fieldType = field.type;
      const dataType = field.configProps?.dataType;
      // Exclude PayableSubContracts (Sub-Contracts for forms) and Submissions (CompletedForms) - HARBOUR-8592
      const excludedDataTypes = ['PayableSubContracts', 'CompletedForms'];
      if (fieldType === 'text' || (fieldType === 'dropdown' && !excludedDataTypes.includes(dataType))) {
        const isRequired = field.configProps?.optional !== true;
        customFieldHeaders.push({
          key: field.title,
          title: `${section.name} - ${field.title}`,
          required: isRequired,
          fieldType: field.type,
          fieldId: field.id,
          sectionId: section.id,
          ...(fieldType === 'dropdown' && {
            dataType: field.configProps?.dataType,
            selectMode: field.configProps?.selectMode,
            caseSensitiveMatch: true,
          }),
        });
      }
    });
  });

  return customFieldHeaders;
};
